import { useConversation } from "@11labs/react";
import { useToast } from "@/hooks/use-toast";
import { useCallback, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const useVoiceFAQChatbot = () => {
  const { toast } = useToast();
  const [conversationId, setConversationId] = useState<string | null>(null);

  const conversation = useConversation({
    onConnect: () => {
      console.log('🎙️ FAQ Voice connected');
      toast({
        title: "Assistant vocal connecté",
        description: "Je suis prêt à répondre à vos questions",
      });
    },
    onDisconnect: () => {
      console.log('🎙️ FAQ Voice disconnected');
      toast({
        title: "Conversation terminée",
        description: "À bientôt !",
      });
    },
    onMessage: (message) => {
      console.log('🎙️ FAQ Message received:', message);
    },
    onError: (error) => {
      console.error('🎙️ FAQ Voice error:', error);
      toast({
        title: "Erreur de conversation",
        description: typeof error === 'string' ? error : 'Une erreur est survenue',
        variant: "destructive",
      });
    },
  });

  const startConversation = useCallback(async () => {
    try {
      console.log('🎙️ Starting FAQ voice conversation...');
      
      // Demander l'accès au microphone
      await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Créer l'agent FAQ via notre edge function
      console.log('🎙️ Creating FAQ agent...');
      const { data: agentData, error } = await supabase.functions.invoke('elevenlabs-faq-agent', {
        body: { 
          gender: 'femme',
          language: 'fr'
        }
      });
      
      if (error) {
        console.error('❌ Edge function error:', error);
        throw new Error('Service temporairement indisponible');
      }
      
      if (!agentData?.success) {
        console.error('❌ Agent creation failed:', agentData?.error);
        toast({
          title: "Assistant vocal indisponible",
          description: agentData?.error || "Impossible de démarrer l'assistant vocal",
          variant: "destructive",
        });
        throw new Error(agentData?.error || 'Failed to create FAQ agent');
      }
      
      // Démarrer la conversation avec l'agent
      console.log('🎙️ FAQ agent created, starting session...');
      const sessionId = await conversation.startSession({ 
        agentId: agentData.agent_id
      });
      
      setConversationId(sessionId || `faq_${Date.now()}`);
      
      console.log('🎙️ FAQ conversation started with ID:', sessionId);
      return sessionId;
      
    } catch (error) {
      console.error('🎙️ Error starting FAQ conversation:', error);
      
      if (!error.message?.includes('indisponible')) {
        toast({
          title: "Erreur",
          description: "Impossible de démarrer la conversation vocale",
          variant: "destructive",
        });
      }
      throw error;
    }
  }, [conversation, toast]);

  const endConversation = useCallback(async () => {
    try {
      await conversation.endSession();
      setConversationId(null);
      console.log('🎙️ FAQ conversation ended');
    } catch (error) {
      console.error('🎙️ Error ending FAQ conversation:', error);
    }
  }, [conversation]);

  const setVolume = useCallback(async (volume: number) => {
    try {
      await conversation.setVolume({ volume });
    } catch (error) {
      console.error('🎙️ Error setting volume:', error);
    }
  }, [conversation]);

  return {
    // États
    isConnected: conversation.status === "connected",
    isSpeaking: conversation.isSpeaking,
    status: conversation.status,
    conversationId,
    
    // Actions
    startConversation,
    endConversation,
    setVolume,
  };
};
